import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const SECTION_TYPES = ["wallet", "giftcard", "referral", "app_section", "credit_line"];

export default function FaqAdminCreate() {
  const navigate = useNavigate();

  const [topic, setTopic] = useState("");
  const [topicOrder, setTopicOrder] = useState("");
  const [sectionType, setSectionType] = useState(SECTION_TYPES[0]);
  const [creditLineRestricted, setCreditLineRestricted] = useState(false);
  const [saving, setSaving] = useState(false);

  const [questions, setQuestions] = useState([{ question: "", answer: "" }]);

  const fetchNextOrder = async () => {
    try {
      const res = await api.get("/admin/faqs/next-topic-order");
      setTopicOrder(String(res.data?.data?.next_topic_order || 1));
    } catch (e) {
      toast.error("Failed to fetch next topic order");
      setTopicOrder("1");
    }
  };

  useEffect(() => {
    fetchNextOrder();
  }, []);

  const addRow = () => setQuestions((p) => [...p, { question: "", answer: "" }]);

  const removeRow = (idx) => {
    if (questions.length === 1) return;
    setQuestions((p) => p.filter((_, i) => i !== idx));
  };

  const onChangeRow = (idx, key, val) => {
    setQuestions((p) => p.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!topic.trim()) return toast.error("Topic is required");
    const bad = questions.some((q) => !q.question.trim() || !q.answer.trim());
    if (bad) return toast.error("All questions and answers are required");

    try {
      setSaving(true);

      await api.post("/admin/faqs", {
        topic,
        topic_order: Number(topicOrder) || undefined,
        section_type: sectionType,
        credit_line_restricted: sectionType === "credit_line" ? creditLineRestricted : false,
        questions,
      });

      toast.success("FAQ topic created");
      navigate("/admin/faqs");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Create FAQ Topic</h1>

        <button
          className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50 self-start"
          onClick={() => navigate("/admin/faqs")}
          type="button"
        >
          Back
        </button>
      </div>

      <form onSubmit={onSubmit} className="mt-6 border rounded-2xl p-5 space-y-4">
        <div>
          <label className="text-sm font-semibold">Topic Name</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Program Overview"
          />
        </div>

        <div className="max-w-[200px]">
          <label className="text-sm font-semibold">Topic Order</label>
          <input
            type="number"
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={topicOrder}
            onChange={(e) => setTopicOrder(e.target.value)}
          />
        </div>

        <div className="max-w-[200px]">
          <label className="text-sm font-semibold">Section Type</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={sectionType}
            onChange={(e) => setSectionType(e.target.value)}
          >
            {SECTION_TYPES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {sectionType === "credit_line" && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="creditLineRestricted"
              checked={creditLineRestricted}
              onChange={(e) => setCreditLineRestricted(e.target.checked)}
            />
            <label htmlFor="creditLineRestricted" className="text-sm font-semibold">
              Visible only to users with credit line enabled
            </label>
          </div>
        )}

        <div className="pt-2">
          <div className="font-semibold mb-2">Questions</div>

          <div className="space-y-3">
            {questions.map((q, idx) => (
              <div key={idx} className="border rounded-2xl p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm">Q{idx + 1}</div>

                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    className="text-xs font-semibold border rounded-lg px-3 py-1 hover:bg-gray-50"
                  >
                    Remove
                  </button>
                </div>

                <input
                  className="w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                  placeholder="Question"
                  value={q.question}
                  onChange={(e) => onChangeRow(idx, "question", e.target.value)}
                />

                <textarea
                  className="w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                  placeholder="Answer"
                  rows={4}
                  value={q.answer}
                  onChange={(e) => onChangeRow(idx, "answer", e.target.value)}
                />
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addRow}
            className="mt-3 rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
          >
            + Add Question
          </button>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "Saving..." : "Create Topic"}
        </button>
      </form>
    </div>
  );
}
